import { action, computed, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { NumberFormatter } from '@estee/elc-formatters';
import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { IServiceAppConfig } from '~domain/entities/IConfig';
import { IPointBenefitContent } from '~exported/loyalty-marketing-point-benefit/ILoyaltyMarketingPointBenefit';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag } from '~utils/Analytics';

export interface IConfigParamsNumberFormats {
    groupingFormatForComma?: boolean;
    numberFormatter?: NumberFormatter;
    pointBenefit: IPointBenefitContent;
}

export interface ILoyaltyMarketingPointBenefitController {
    loyaltyRepository: LoyaltyRepository;
    numberFormatter: NumberFormatter;
    configs: IServiceAppConfig;
}

class LoyaltyMarketingPointBenefitController extends ViewController {
    private loyaltyRepository: LoyaltyRepository;
    private numberFormatter: NumberFormatter;
    private readonly configs: IServiceAppConfig;

    @observable private configParams: IConfigParamsNumberFormats;
    @observable currentTabIndex: string;

    constructor({
        configs,
        loyaltyRepository,
        numberFormatter
    }: ILoyaltyMarketingPointBenefitController) {
        super();
        this.configs = configs;
        this.loyaltyRepository = loyaltyRepository;
        this.numberFormatter = numberFormatter;
    }

    @computed
    public get formattedPoints() {
        const pointsValue = this.loyaltyRepository?.loyaltyInfo?.currentPoints;

        return this.numberFormatter.formatNumber(pointsValue, {
            shouldUseGrouping: this.configs.loyaltyConfig?.groupingFormatForComma
        });
    }

    @action
    public formattedCurrentPoints(value: { text: string }, currentPoints: string) {
        return value?.text && currentPoints
            ? value.text.replace('::currentPoints::', currentPoints)
            : '';
    }

    @computed
    public get formattedPointsInfoDescription() {
        const { pointsInfo } = this.configParams.pointBenefit;

        return this.formattedCurrentPoints(pointsInfo, this.formattedPoints);
    }

    @action
    public setCurrentTabId = (tabId: string) => {
        this.currentTabIndex = tabId;
        const elabel = tabId?.toUpperCase().includes('POINTS') ? tabId : `${tabId} POINTS`;
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'section_title_click', event_label: elabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    };

    @action
    public setConfigParams = (configParams: IConfigParamsNumberFormats) => {
        this.configParams = configParams;
    };

    @action
    public onArrowClick(eLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'arrow_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    @computed
    get currentTabId() {
        return this.currentTabIndex;
    }

    @computed
    get displayPointsForLoyalty() {
        return this.loyaltyRepository.isLoyaltyUser;
    }
}

export default LoyaltyMarketingPointBenefitController;
